// Types
import { MARKETPLACE_ID, type OrderApiData, type OrderData } from "@swiggy-private/payment-types";
import { OrderPlaceParams } from "@payment-client/interfaces/OrderPublic";
import {
    type InstamartInitiatePaymentRequest,
    type InstamartInitiatePaymentParams,
    type InstamartPaymentResponse,
    type InstamartConfirmPaymentRequest,
    PaymentTransactionState,
    type TransactionEntry,
    PaymentContext,
} from "@payment-client/apis/instamartPaymentService";

// Utils
import { paymentMethodObjToCode } from "@payment-client/helpers/AppRedirectionHelper";

/* eslint-disable @typescript-eslint/naming-convention */
export const buildInstamartPayOnDeliveryInitiateRequestBody = (
    orderId: string,
    params: OrderPlaceParams,
): InstamartInitiatePaymentRequest => {
    const paymentCode = params.payment_cod_method;
    const parsedAmount = Number(params.transaction_amount);
    const transactionAmount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
    const paymentInfo = params.payment_info as InstamartInitiatePaymentParams["payment_info"];
    const paymentMethodMeta = paymentInfo?.metadata ?? "";

    return {
        order_id: orderId,
        payment_info: {
            payment_method: paymentCode as string,
            metadata: paymentMethodMeta,
            transaction_amount: transactionAmount,
        },
    };
};
/* eslint-enable @typescript-eslint/naming-convention */

function toPaymentTxnStatus(state?: string): string {
    switch (state) {
        case PaymentTransactionState.STATE_SUCCESS:
            return "successful";
        case PaymentTransactionState.STATE_PENDING:
        case PaymentTransactionState.STATE_INIT:
            return "pending";
        default:
            return "failed";
    }
}

// Transforms a PAAS payment response (initiate/confirm) directly into OrderApiData.
// This transformer handles both initiate and confirm payment responses for Instamart pay on delivery.
export const transformInstamartPayOnDeliveryPaymentResponse = (
    orderId: string,
    instamartResponse: InstamartPaymentResponse,
): OrderApiData => {
    const txnId = instamartResponse.payment?.marketplaceReferenceId ?? "";
    const paymentId = instamartResponse.paymentId;
    const paymentTxnStatus = toPaymentTxnStatus(instamartResponse.payment?.state);

    const paymentInfo: TransactionEntry = {
        paymentTxnId: paymentId,
        paymentPartnerOrderId: paymentId,
        paymentTxnStatus,
    };

    const appRedirectionInfo = instamartResponse.paymentMethodRedirect
        ? JSON.stringify(instamartResponse.paymentMethodRedirect)
        : undefined;

    const paymentMethodType = instamartResponse.paymentMethodRedirect?.paymentMethodType;
    const paymentMethodCode = paymentMethodObjToCode(paymentMethodType as Parameters<typeof paymentMethodObjToCode>[0]);
    const transactionAmount = Number(instamartResponse.payment?.totalAmount?.units ?? 0);

    const orderData = {
        orderId,
        orderType: MARKETPLACE_ID.DASH,
        orderItems: [],
        orderTotal: transactionAmount,
        orderPaymentMethod: paymentMethodCode,
        orderStatus: paymentTxnStatus,
        postStatus: paymentTxnStatus,
        ...paymentInfo,
        appRedirectionInfo,
        targetedMarketplacePaymentReferenceId: txnId,

        // Add polling configuration
        ...(instamartResponse.paymentMethodRedirect?.pollingConfiguration && {
            maxTimeToPollForInMs: instamartResponse.paymentMethodRedirect.pollingConfiguration.maxTimeToPollForInMs,
            pollingIntervalInMs: instamartResponse.paymentMethodRedirect.pollingConfiguration.pollingIntervalInMs,
        }),
    } as unknown as OrderData;

    return {
        orderData,
        retryDelayTimeInMs: 0,
        retryCount: 0,
    };
};

/* eslint-disable @typescript-eslint/naming-convention */
export const buildInstamartPayOnDeliveryConfirmRequestBody = (
    orderId: string,
    paymentId: string,
): InstamartConfirmPaymentRequest => {
    return {
        order_id: orderId,
        payment_id: paymentId,
        payment_context: PaymentContext.PAYMENT_CONTEXT_TRACK_PAGE_PAYMENT,
        no_refund_confirmation: false,
    };
};
/* eslint-enable @typescript-eslint/naming-convention */
